import { api } from '@/lib/api'
import { useEffect, useRef } from 'react'
import { io, Socket } from 'socket.io-client'

type AgendaRealtimeAction = 'created' | 'updated' | 'deleted'

interface AgendaUpdatedEvent {
  eventId: string
  action: AgendaRealtimeAction
  agendaId: number
  idEmpresa: number
  occurredAt: string
}

interface UseAgendaRealtimeOptions<T> {
  enabled?: boolean
  mapApiItem: (item: any) => T
  onUpsertMany: (items: T[]) => void
  onDelete: (agendaId: number) => void
  onFullSync: () => Promise<void>
}

const BATCH_DEBOUNCE_MS = 350
const SAFETY_SYNC_INTERVAL_MS = 90_000
const HIGH_VOLUME_THRESHOLD = 15
const EVENT_TTL_MS = 2 * 60_000

function obterToken(): string | null {
  return (
    localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
  )
}

export function useAgendaRealtime<T>({
  enabled = true,
  mapApiItem,
  onUpsertMany,
  onDelete,
  onFullSync
}: UseAgendaRealtimeOptions<T>): void {
  const socketRef = useRef<Socket | null>(null)
  const pendingIdsRef = useRef<Set<number>>(new Set())
  const debounceRef = useRef<number | null>(null)
  const safetySyncRef = useRef<number | null>(null)
  const vistosRef = useRef<Map<string, number>>(new Map())
  const connectedOnceRef = useRef(false)
  const disconnectedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return

    const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
    const token = obterToken()

    if (!baseUrl || !token) {
      return
    }

    const limparEventosExpirados = () => {
      const agora = Date.now()
      for (const [eventId, ts] of vistosRef.current.entries()) {
        if (agora - ts > EVENT_TTL_MS) {
          vistosRef.current.delete(eventId)
        }
      }
    }

    const processarPendentes = async () => {
      const ids = Array.from(pendingIdsRef.current)
      pendingIdsRef.current.clear()

      if (ids.length === 0) {
        return
      }

      if (ids.length > HIGH_VOLUME_THRESHOLD) {
        await onFullSync()
        return
      }

      try {
        const resposta = await api.get(`/api/agenda/lote?ids=${ids.join(',')}`)
        const itens = Array.isArray(resposta?.result) ? resposta.result : []
        onUpsertMany(itens.map(mapApiItem))
      } catch (error) {
        console.error('Erro ao sincronizar lote da agenda via socket:', error)
        await onFullSync()
      }
    }

    const agendarProcessamento = () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current)
      }

      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null
        void processarPendentes()
      }, BATCH_DEBOUNCE_MS)
    }

    const socket = io(baseUrl, {
      transports: ['websocket'],
      withCredentials: true,
      auth: {
        token: `Bearer ${token}`
      }
    })

    socketRef.current = socket

    socket.on('connect', () => {
      if (connectedOnceRef.current && disconnectedRef.current) {
        disconnectedRef.current = false
        void onFullSync()
      }

      connectedOnceRef.current = true
    })

    socket.on('disconnect', () => {
      disconnectedRef.current = true
    })

    socket.on('agenda:updated', (evento: AgendaUpdatedEvent) => {
      if (!evento || !evento.eventId) return

      limparEventosExpirados()
      if (vistosRef.current.has(evento.eventId)) {
        return
      }

      vistosRef.current.set(evento.eventId, Date.now())

      if (!Number.isInteger(evento.agendaId) || evento.agendaId <= 0) {
        return
      }

      if (evento.action === 'deleted') {
        onDelete(evento.agendaId)
        return
      }

      if (evento.action === 'created' || evento.action === 'updated') {
        pendingIdsRef.current.add(evento.agendaId)
        agendarProcessamento()
      }
    })

    safetySyncRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void onFullSync()
      }
    }, SAFETY_SYNC_INTERVAL_MS)

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current)
      }

      if (safetySyncRef.current) {
        window.clearInterval(safetySyncRef.current)
      }

      socket.off('agenda:updated')
      socket.disconnect()
      socketRef.current = null
      pendingIdsRef.current.clear()
      vistosRef.current.clear()
      disconnectedRef.current = false
      connectedOnceRef.current = false
    }
  }, [enabled, mapApiItem, onDelete, onFullSync, onUpsertMany])
}
